import React, { useEffect, useState } from 'react'

import { Field,Form } from 'react-final-form'
import { connect } from 'react-redux'

import FormDropDownField from 'components/FormDropDownField/FormDropDownField'
import FormInputField from 'components/FormInputField/FormInputField'
import { FormSwitch } from 'components/FormSwitch/FormSwitch'
import Spinner from 'components/Spinner/Spinner'
import { actions as managePointsActions } from 'coupon-common/src/modules/admin/pointsManager/managePoints';
import { actions as locationActions } from 'coupon-common/src/modules/locations';
import { actions as plansActions } from 'coupon-common/src/modules/memberships/plans';
import { required } from 'coupon-common/src/utils';

import styles from './PromoCodesForm.module.scss'

const DISCOUNT_TYPE = {
    AMOUNT: 'amount',
    PERCENT: 'percent',
};

const TARGET_TYPE = {
    MEMBERSHIP: 'membership',
    PRODUCT: 'product',
};

const PromoCodesForm = React.memo(({
    initialValues = {},
    mode = "create",
    onSubmit,
    users,
    dispatch,
    memberships,
    provinces,
    cities,
    loading,
}) => {

    const [formInitialValues, setFormInitialValues] = useState({});
    const [discountType, setDiscountType] = useState(DISCOUNT_TYPE.PERCENT);
    const [targetType, setTargetType] = useState(TARGET_TYPE.MEMBERSHIP);
    const [restrictedTo, setRestrictedTo] = useState([]);
    const [businessInput, setBusinessInput] = useState('');
    const [businessOptions, setBusinessOptions] = useState([])
    const [provinceOptions, setProvinceOptions] = useState([])
    const [citiesOptions, setCitiesOptions] = useState([])
    const [allCities, setAllCities] = useState([]);

    useEffect(() => {
        dispatch(managePointsActions.getAllUsers());
        dispatch(plansActions.getMembershipPlans());
        dispatch(locationActions.getProvinces());
        dispatch(locationActions.getCities());
    }, [])

    useEffect(() => {
        if (users?.length) {
            const options = users.filter((user) => user.is_business) // ✅ only businesses


            setBusinessOptions(options);
        }
    }, [users]);

    useEffect(() => {
        
        if (provinces && Object.keys(provinces).length) {
            const provinceArray = Object.values(provinces);

            setProvinceOptions(
                provinceArray
            )
        }

        if (cities && Object.keys(cities).length) {
            const citiesArray = Object.values(cities);
            setAllCities(citiesArray)
            setCitiesOptions(citiesArray)

        }
    }, [provinces, cities]);

    // 🔥 Sync state for edit mode
    useEffect(() => {
        if (mode === "edit" && initialValues) {
            const normalized = {
                ...initialValues,

                // ✅ convert to YYYY-MM-DD
                valid_until: initialValues.valid_until
                    ? new Date(initialValues.valid_until)
                        .toISOString()
                        .split("T")[0]
                    : "",
            };

            setFormInitialValues(normalized);

            // Discount type
            if (initialValues.discount_amount) {
                setDiscountType(DISCOUNT_TYPE.AMOUNT);
            } else {
                setDiscountType(DISCOUNT_TYPE.PERCENT);
            }

            // Target type
            if (initialValues.product_id) {
                setTargetType(TARGET_TYPE.PRODUCT);
            } else {
                setTargetType(TARGET_TYPE.MEMBERSHIP);
            }

            // ✅ Filter cities based on existing province selection in edit mode
            if (initialValues.restricted_to_provinces?.length) {
                // let filteredCities = []
                const provinceIds = initialValues.restricted_to_provinces.map(p =>
                    typeof p === "object" ? p.id : p  // handle both {id, name} and raw id
                );
                const filtered = allCities.filter(city =>
                    provinceIds.includes(city.province)
                );
                setCitiesOptions(filtered);
            }
        }
    }, [initialValues, mode, allCities]);

    const handleDiscountTypeToggle = (type, form) => {
        setDiscountType(type);
        form.change('discount_amount', '');
        form.change('discount_percent', '');
    };

    const handleTargetToggle = (type, form) => {
        setTargetType(type);

        if (type === TARGET_TYPE.MEMBERSHIP) {
            form.change('product_id', null);
        } else {
            form.change('mem_id', null);
        }
    };

    const handleAddBusiness = () => {
        const id = parseInt(businessInput.trim());
        if (!id || restrictedTo.includes(id)) return;

        setRestrictedTo((prev) => [...prev, id]);
        setBusinessInput('');
    };

    const handleRemoveBusiness = (id) => {
        setRestrictedTo((prev) => prev.filter((b) => b !== id));
    };

    const handleSubmit = (values) => {
      
        const payload = {
            code: values.code?.trim(),

            // ✅ normalize numbers
            discount_amount: values.discount_amount
                ? Number(values.discount_amount)
                : null,

            discount_percent: values.discount_percent
                ? Number(values.discount_percent)
                : null,

            max_uses: values.max_uses ? Number(values.max_uses) : null,
            max_discount_cycles: values.max_discount_cycles
                ? Number(values.max_discount_cycles)
                : null,
            // ✅ booleans
            is_active: values.is_active ?? true,

            // ✅ product targeting
            mem_id: values.mem_id || null,
            product_id: values.product_id || null,

            // ✅ restricted_to → extract IDs if objects
            restricted_to: Array.isArray(values.restricted_to)
                ? values.restricted_to.map((b) => b.value || b.id)
                : [],
            restricted_to_provinces: Array.isArray(values.restricted_to_provinces)
                ? values.restricted_to_provinces.map((p) => p.value || p.id)
                : [],
            restricted_to_cities: Array.isArray(values.restricted_to_cities)
                ? values.restricted_to_cities.map((c) => c.value || c.id)
                : [],



        };

        // 🔥 ensure ONLY one discount type
        if (payload.discount_amount) {
            payload.discount_percent = null;
        } else if (payload.discount_percent) {
            payload.discount_amount = null;
        }

        // 🔥 normalize date (end of day)
        if (values.valid_until) {
            const d = new Date(values.valid_until);

            // ✅ set end of day
            d.setHours(23, 59, 59, 999);

            payload.valid_until = d.toISOString();
        } else {
            payload.valid_until = null;
        }


        if (onSubmit) {
            onSubmit(payload);
        }
    };

    const handleProvinceChange = (selectedProvinces) => {
        
        // If nothing selected → show all cities
        if (!selectedProvinces || selectedProvinces.length === 0) {
            setCitiesOptions(allCities);
            return;
        }

        const selectedProvinceIds = selectedProvinces.map(p => p.value);

        
        const filteredCities = allCities.filter(city =>
            selectedProvinceIds.includes(city.province)
        );


        setCitiesOptions(filteredCities);
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.formCard}>

                {/* Header */}
                <div className={styles.cardHeader}>
                    <div className={styles.headerIcon}>%</div>
                    <div>
                        <h3 className={styles.cardTitle}>
                            {mode === "edit" ? "Edit Promo Code" : "Create Promo Code"}
                        </h3>
                        <p className={styles.cardSubtitle}>
                            {mode === "edit"
                                ? "Update existing promo code"
                                : "Set up a new discount code for businesses"}
                        </p>
                    </div>
                </div>

                <Form
                    onSubmit={handleSubmit}
                    initialValues={{
                        is_active: true,
                        ...formInitialValues
                    }}
                    render={({ handleSubmit: formSubmit, form, values }) => (
                        <form onSubmit={formSubmit}>
                            <div className={styles.formBody}>

                                {/* Code + Active */}
                                <div className={styles.row}>
                                    <div className={styles.flexGrow}>
                                        <FormInputField
                                            name="code"
                                            label="Promo Code"
                                            placeholder="e.g. WINTER30"
                                            maxLength={50}
                                            validate={required}
                                            required
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.label}>Status</label>
                                        <Field name="is_active">
                                            {({ input }) => (
                                                <FormSwitch
                                                    label={input.value ? 'Active' : 'Inactive'}
                                                    checked={input.value}
                                                    onChange={input.onChange}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                </div>

                                {/* Discount Type Toggle */}
                                <div className={styles.field}>
                                    <label className={styles.label}>
                                        Discount Type <span className={styles.required}>*</span>
                                    </label>
                                    <div className={styles.segmentedControl}>
                                        <button
                                            type="button"
                                            className={`${styles.segment} ${discountType === DISCOUNT_TYPE.PERCENT ? styles.segmentActive : ''}`}
                                            onClick={() => handleDiscountTypeToggle(DISCOUNT_TYPE.PERCENT, form)}
                                        >
                                            Percentage (%)
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.segment} ${discountType === DISCOUNT_TYPE.AMOUNT ? styles.segmentActive : ''}`}
                                            onClick={() => handleDiscountTypeToggle(DISCOUNT_TYPE.AMOUNT, form)}
                                        >
                                            Flat Amount ($)
                                        </button>
                                    </div>
                                </div>

                                {/* Discount Value */}
                                <div className={styles.field}>
                                    <FormInputField
                                        label={discountType === DISCOUNT_TYPE.PERCENT
                                            ? 'Discount Percentage'
                                            : 'Discount Amount'}
                                        name={discountType === DISCOUNT_TYPE.PERCENT
                                            ? 'discount_percent'
                                            : 'discount_amount'}
                                        type="number"
                                        placeholder={discountType === DISCOUNT_TYPE.PERCENT
                                            ? 'Ex. 25%'
                                            : 'Ex. $30.00'}
                                        validate={required}
                                    />
                                </div>

                                {/* 🔥 Apply To Toggle */}
                                <div className={styles.field}>
                                    <label className={styles.label}>
                                        Apply To <span className={styles.required}>*</span>
                                    </label>

                                    <div className={styles.segmentedControl}>
                                        <button
                                            type="button"
                                            className={`${styles.segment} ${targetType === TARGET_TYPE.MEMBERSHIP ? styles.segmentActive : ''}`}
                                            onClick={() => handleTargetToggle(TARGET_TYPE.MEMBERSHIP, form)}
                                        >
                                            Memberships
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.segment} ${targetType === TARGET_TYPE.PRODUCT ? styles.segmentActive : ''}`}
                                            onClick={() => handleTargetToggle(TARGET_TYPE.PRODUCT, form)}
                                        >
                                            Products
                                        </button>
                                    </div>
                                </div>

                                {/* Conditional Rendering */}
                                {targetType === TARGET_TYPE.MEMBERSHIP && (
                                    <>
                                        <div className={styles.field}>
                                            <FormDropDownField
                                                name="mem_id"
                                                label="Memberships"
                                                validate={required}
                                                required
                                                options={memberships}
                                                searchable
                                                mapOption={(p) => ({
                                                    value: p.mem_id,
                                                    label: p.name,
                                                })}
                                            />
                                        </div>
                                        <div className={styles.field}>
                                            <p className={styles.fieldHint}>Leave blank for no expiry</p>
                                            <FormInputField
                                                name="max_discount_cycles"
                                                label="Max Discount Cycles"
                                                placeholder="e.g. 3"
                                                type={Number}
                                            // validate={required}
                                            // required
                                            />
                                        </div>
                                    </>
                                )}

                                {targetType === TARGET_TYPE.PRODUCT && (
                                    <div className={styles.field}>

                                        <FormDropDownField
                                            name="product_id"
                                            label="Product"
                                            validate={required}
                                            required
                                            options={[
                                                { value: 1, label: "Product A" },
                                                { value: 2, label: "Product B" },
                                                { value: 3, label: "Product C" },
                                            ]}
                                            searchable
                                        // mapOption={(p) => ({
                                        //     value: p.id,
                                        //     label: p.name,
                                        // })}
                                        />
                                    </div>
                                )}


                                {/* Valid Until + Max Uses */}
                                <div className={styles.row}>
                                    <div className={styles.flexGrow}>
                                        <p className={styles.fieldHint}>Leave blank for no expiry</p>
                                        <FormInputField
                                            name="valid_until"
                                            label="Valid Until"
                                            type="date"
                                        />
                                    </div>
                                    <div className={styles.flexGrow}>
                                        <p className={styles.fieldHint}>Leave blank for unlimited</p>
                                        <FormInputField
                                            name="max_uses"
                                            label="Max Uses"
                                            type="number"
                                        />
                                    </div>
                                </div>

                                {/* Business Restrictions */}
                                <div className={styles.field}>

                                    <p className={styles.fieldHint}>
                                        Leave empty to allow all businesses
                                    </p>

                                    <FormDropDownField
                                        name="restricted_to"
                                        label="Restrict to Businesses"
                                        options={businessOptions}
                                        searchable
                                        isMulti
                                        mapOption={(b) => ({
                                            value: b.id,
                                            label: b.name || b.email,
                                        })}
                                    />
                                </div>

                                <div className={styles.field}>

                                    <p className={styles.fieldHint}>
                                        Leave empty to allow all provinces
                                    </p>

                                    <FormDropDownField
                                        name="restricted_to_provinces"
                                        label="Restrict to province"
                                        options={provinceOptions}
                                        searchable
                                        isMulti
                                        mapOption={(b) => ({
                                            value: b.id,
                                            label: b.name || b.abbreviation,
                                        })}
                                        onChange={handleProvinceChange}
                                    />
                                </div>

                                <div className={styles.field}>
                                    {values?.restricted_to_provinces?.length > 0 && (
                                        <div className={styles.field}>
                                            <p className={styles.fieldHint}>
                                                Leave empty to allow all cities
                                            </p>

                                            <FormDropDownField
                                                name="restricted_to_cities"
                                                label="Restrict to cities"
                                                options={citiesOptions}
                                                searchable
                                                isMulti
                                                mapOption={(b) => ({
                                                    value: b.id,
                                                    label: b.name,
                                                })}
                                            />
                                        </div>
                                    )}
                                    {/* <p className={styles.fieldHint}>
                                        Leave empty to allow all cities
                                    </p>

                                    <FormDropDownField
                                        name="restricted_to_cities"
                                        label="Restrict to cities"
                                        options={citiesOptions}
                                        searchable
                                        isMulti
                                        mapOption={(b) => ({
                                            value: b.id,
                                            label: b.name,
                                        })}
                                    /> */}
                                </div>

                                {/* Create new component for buttons or add an option for style2 in existing button component */}

                                {/* Actions */}
                                <div className={styles.actions}>
                                    <button
                                        type="button"
                                        className={styles.resetButton}
                                        onClick={() => {
                                            form.reset();
                                            setDiscountType(DISCOUNT_TYPE.PERCENT);
                                            setTargetType(TARGET_TYPE.MEMBERSHIP);
                                            setRestrictedTo([]);
                                            setBusinessInput('');
                                        }}
                                    >
                                        Reset
                                    </button>

                                    <button
                                        type="submit"
                                        className={styles.submitButton}
                                    >   {loading ?
                                        <Spinner show={true} color={'light'} size={'sm'}/> :
                                        (
                                            <>

                                                {mode === "edit"
                                                    ? "Update Promo Code"
                                                    : "Create Promo Code"}
                                            </>
                                        )
                                        }
                                    </button>
                                </div>

                            </div>
                        </form>
                    )}
                />
            </div>
        </div>
    )
})

// export default PromoCodesForm;

const mapStateToProps = (state) => ({
    users: state.admin.pointsManager.managePoints.getUsers.data || [],
    memberships: state.memberships.plans.data || [],
    provinces: state.locations.entities.provinces || [],
    cities: state.locations.entities.cities || []
});
export default connect(mapStateToProps)(PromoCodesForm);